import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import AuthPage, { inputClass, labelClass, primaryBtn } from "@/components/account/AuthPage";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { useSeo } from "@/lib/seo";

export default function Login() {
  const { t, lang } = useLang();
  useSeo({ title: `${t("auth.loginTitle")} – Nivå Digital`, description: t("auth.loginSub"), noindex: true });
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [unverified, setUnverified] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setUnverified(false);
    setLoading(true);
    try {
      const u = await login(email, password);
      const next = params.get("next");
      if (next && next.startsWith("/") && !next.startsWith("//")) navigate(next);
      else navigate(u.role === "admin" ? "/admin" : "/mitt-konto");
    } catch (err) {
      if (err?.response?.status === 403) setUnverified(true);
      else setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    try {
      await api.post("/auth/resend-verification", { email, lang });
      toast.success(t("auth.resent"));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <AuthPage tag={t("nav.account")} title={t("auth.loginTitle")} sub={t("auth.loginSub")} testid="login-page">
      <form data-testid="login-form" onSubmit={submit} className="space-y-5">
        <div>
          <label htmlFor="login-email" className={labelClass}>{t("auth.email")}</label>
          <input id="login-email" data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} autoComplete="email" />
        </div>
        <div>
          <label htmlFor="login-password" className={labelClass}>{t("auth.password")}</label>
          <input id="login-password" data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputClass} autoComplete="current-password" />
        </div>
        {error && (
          <p data-testid="login-error" className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-400">{error}</p>
        )}
        {unverified && (
          <div data-testid="login-unverified" className="rounded-lg border border-mint/30 bg-mint/10 px-4 py-3 text-sm text-bone">
            <p>{t("auth.notVerified")}</p>
            <button type="button" data-testid="login-resend-button" onClick={resend} className="mt-2 font-semibold text-mint hover:underline">
              {t("auth.resend")}
            </button>
          </div>
        )}
        <button data-testid="login-submit" type="submit" disabled={loading} className={primaryBtn}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("auth.loginBtn")}
        </button>
        <p className="text-center text-sm">
          <Link data-testid="login-forgot-link" to="/glomt-losenord" className="text-fog transition-colors hover:text-mint">{t("auth.forgot")}</Link>
        </p>
        <p className="text-center text-sm text-fog">
          {t("auth.noAccount")}{" "}
          <Link data-testid="login-register-link" to="/registrera" className="font-semibold text-mint hover:underline">{t("auth.registerLink")}</Link>
        </p>
      </form>
    </AuthPage>
  );
}
